import React, { useState, useRef, useEffect } from 'react';
import { Project } from '../types';
import { FolderIcon, ChevronDownIcon, PlusCircleIcon } from './icons';

interface ProjectSwitcherProps {
  projects: Project[];
  currentProject: Project | null;
  setCurrentProjectId: (id: string) => void;
  onOpenCreateModal: () => void;
}

const ProjectSwitcher: React.FC<ProjectSwitcherProps> = ({ projects, currentProject, setCurrentProjectId, onOpenCreateModal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show shimmer placeholder until current project resolves (avoids "No Projects" flash)
  if (!currentProject) {
    return (
      <div className="flex items-center gap-2 text-left p-1.5 rounded-lg">
        <div>
          <div className="h-2 w-12 bg-neutral-200 animate-pulse rounded"></div>
          <div className="h-4 w-24 bg-neutral-200 animate-pulse rounded mt-1"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-w-0" ref={dropdownRef} data-tutorial-id="project-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-left p-1.5 rounded-lg hover:bg-app-bg active:bg-app-bg transition-colors w-full min-w-0"
      >
        <div className="min-w-0 flex-1 flex flex-col justify-center">
          <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold leading-none mb-0.5">Project</p>
          <h2 className="text-[14px] font-bold text-text-ink leading-none truncate">{currentProject.name}</h2>
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-text-faint transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 w-64 bg-white rounded-lg shadow-soft-lift border border-app-border z-50">
          <ul className="p-2 max-h-64 overflow-y-auto">
            {projects.map(project => (
              <li key={project.id}>
                <button
                  onClick={() => {
                    setCurrentProjectId(project.id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${project.id === currentProject.id
                    ? 'bg-action text-white'
                    : 'text-text-ink hover:bg-app-bg active:bg-app-bg'
                    }`}
                >
                  {project.name}
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-app-border p-2">
            <button
              onClick={() => {
                onOpenCreateModal();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm font-medium text-action hover:bg-action/5 active:bg-action/10"
            >
              <PlusCircleIcon className="w-5 h-5" />
              Create New Project
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSwitcher;